import { useEffect } from 'react';
import { canonicalFor, getLanguagePagePair } from '../lib/site';

function upsertMeta(selector, attr, name, content) {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

/**
 * Mantém title, description, canonical e og sincronizados na navegação
 * client-side. O HTML pré-renderizado de cada URL já sai correto do build;
 * este hook cobre a troca de rota sem recarregar a página.
 */
export function useSEO({ title, description, path, noindex = false, locale = 'pt' }) {
  useEffect(() => {
    if (title) {
      document.title = title;
      upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    }

    if (description) {
      upsertMeta('meta[name="description"]', 'name', 'description', description);
      upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    }

    if (path) {
      const url = canonicalFor(path, locale);
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', url);
      upsertMeta('meta[property="og:url"]', 'property', 'og:url', url);
      document.documentElement.lang = locale === 'es' ? 'es' : 'pt-BR';
      upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', locale === 'es' ? 'es_ES' : 'pt_BR');
      document.querySelectorAll('link[rel="alternate"][hreflang="pt-BR"], link[rel="alternate"][hreflang="es"], link[rel="alternate"][hreflang="x-default"]').forEach((tag) => tag.remove());
      const pair = getLanguagePagePair(path);
      if (pair) {
        const [portuguesePath, spanishPath] = pair;
        [
          ['pt-BR', canonicalFor(portuguesePath, 'pt')],
          ['es', canonicalFor(spanishPath, 'es')],
          ['x-default', canonicalFor('/es', 'es')],
        ].forEach(([language, href]) => {
          const alternate = document.createElement('link');
          alternate.rel = 'alternate';
          alternate.hreflang = language;
          alternate.href = href;
          document.head.appendChild(alternate);
        });
      }
    }

    const robotsContent = noindex
      ? 'noindex, follow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    upsertMeta('meta[name="robots"]', 'name', 'robots', robotsContent);
  }, [title, description, path, noindex, locale]);
}
